import * as Sentry from '@sentry/react'
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useEffectOnce } from 'react-use'
import store from 'store'

import agent from 'agent'
import {
  AuthContextValues,
  BASE_SESSION,
  DEFAULT_AUTH_VALUES,
  getActiveTeamIdFromLocalStorage,
  getEntitlementsForAuth,
  getHasMigratedPrivilege,
  getRbacSummary,
  getUserSession,
  renewUserSession,
  setActiveTeamIdInLocalStorage,
  TEAM_DETAILS_FALLBACK
} from 'context/auth-context-utils'
import { resetAnalytics } from 'track'
import {
  RbacBody,
  RbacPrivilege,
  RbacRoleCompany,
  RbacRoleGremlinInternalCompany
} from 'types/api/rbac/rbac'
import {
  UserSession,
  UserSessionTeam
} from 'types/api/users/sessions/user-session'
import { ms } from 'utils/millisecond'
import { RBAC as RBACFallback } from 'utils/rbac-typed'
import { getSessionData, NO_TEAM } from 'utils/session'

export { DEFAULT_AUTH_VALUES, NO_TEAM }

export const AuthContext = createContext(DEFAULT_AUTH_VALUES)

export const AuthProvider: React.FC<{
  children: React.ReactNode
  initialCurrentTeamId?: string
  initialIsRbacEnabled?: boolean
  initialRbac?: RbacBody
  initialReady?: boolean
  initialSession?: UserSession
}> = ({
  initialSession = BASE_SESSION,
  initialReady = false,
  initialCurrentTeamId = '',
  initialIsRbacEnabled = false,
  initialRbac = RBACFallback,
  children
}) => {
  const [authState, setAllAuth] = useState(
    /**
     * Use state initializer function
     * See: https://react.dev/reference/react/useState#avoiding-recreating-the-initial-state
     * */
    () => ({
      ready: initialReady,
      session: initialSession,
      currentTeamId: initialCurrentTeamId,
      isRbacEnabled: initialIsRbacEnabled,
      RBAC: initialRbac
    })
  )

  const { session, ready, currentTeamId, isRbacEnabled, RBAC } = authState

  const endSession = useCallback(async () => {
    if (session.header) {
      try {
        await agent.delete('/users/auth')
      } catch (err) {
        /* We want to wipe out all local storage and log the user out despite the success/failure of the agent.delete('/users/auth') call */
      }

      /**
       * The following action wipes out all local storage persistence without discretion, whereas the
       *  need to persist the user preference state for alert visibility is needed. This single key
       *  is temporarily stored and re-applied following the clear() call.
       */
      const alertPreferencesPersistence = store.get('alertPreferences')
      const redirectPathPersistence = store.get('loginRedirect')
      const lastActiveOrgId = store.get('lastActiveOrgId')
      // we store some user settings in localStorage to survive page navigations
      // so make sure that's cleared out on logout
      window.localStorage.clear()
      // re-applying the user alert preference object
      store.set('alertPreferences', alertPreferencesPersistence)
      store.set('loginRedirect', redirectPathPersistence)
      // persist last team id
      store.set('lastActiveOrgId', lastActiveOrgId)
    }

    // clear segment.com analytics data
    resetAnalytics()
    /**
     * TODO: These calls are potentially redundant if followed by the explicit localStorage.clear() call
     * above, and a new design should be considered to persist local data and delete necessary ones.
     */
    store.remove('session')
    store.remove('onboardingAccessed')
    // discard the choice of resetting secret key when user log out
    store.remove('confirmSecretReset')
    store.remove('pendingEnvironmentRequestID')
    setAllAuth((current) => ({
      ...current,
      ready: true,
      session: BASE_SESSION,
      currentTeamId: '',
      isRbacEnabled: false
    }))
  }, [session.header, setAllAuth])

  const startSession = useCallback(
    async (
      activeSession: UserSession = store.get('session'),
      activeTeamId?: string
    ) => {
      const hasSession = activeSession?.company_id

      activeTeamId =
        activeTeamId ||
        getActiveTeamIdFromLocalStorage(hasSession ?? currentTeamId)

      if (!hasSession) {
        setAllAuth((current) => ({
          ...current,
          ready: true
        }))
        return
      }

      const isExpired = new Date(activeSession.expires_at) <= new Date()
      let updatedSession = activeSession
      try {
        if (isExpired) {
          updatedSession = await renewUserSession(activeSession)
        } else {
          updatedSession = await getUserSession(activeSession)
        }
      } catch (e) {
        return endSession()
      }

      const { sessionToStart, activeTeam } = getSessionData(
        updatedSession,
        activeTeamId
      )

      // if (process.env.NODE_ENV === 'production') {
      Sentry.setUser({
        email: sessionToStart.identifier,
        id: sessionToStart.identifier
      })
      Sentry.setContext('session', {
        teamId: activeTeam.identifier,
        companyId: activeSession?.company_id
      })
      // }

      const rbacSummary = await getRbacSummary(sessionToStart)

      setAllAuth({
        currentTeamId: activeTeam.identifier,
        isRbacEnabled: true,
        RBAC: rbacSummary,
        ready: true,
        session: sessionToStart
      })

      store.set('session', sessionToStart)
      setActiveTeamIdInLocalStorage(
        activeTeam.identifier,
        updatedSession.company_id
      )
      return sessionToStart
    },
    [endSession, currentTeamId]
  )

  const renewSession = useCallback(async () => {
    try {
      const nextSession = await startSession(await renewUserSession(session))
      return nextSession
    } catch (err) {
      endSession()
    }
  }, [endSession, session, startSession])

  const fetchSession = useCallback(async () => {
    try {
      const nextSession = await startSession(session)
      return nextSession
    } catch (e) {
      endSession()
    }
  }, [endSession, session, startSession])

  const setActiveTeamId = useCallback(
    async (teamId: string) => {
      setAllAuth((current) => ({
        ...current,
        currentTeamId: teamId
      }))

      setActiveTeamIdInLocalStorage(teamId, session.company_id)
    },
    [session.company_id]
  )

  const getTeamDetails = useCallback(
    (teamId: string) => {
      return (session.teams.find((t) => t.identifier === teamId) ||
        TEAM_DETAILS_FALLBACK) as UserSessionTeam
    },
    [session.teams]
  )

  const hasCompanyPrivilege = useCallback(
    (privilege: RbacPrivilege, shouldAutoMigrate = true) =>
      getHasMigratedPrivilege(session.privileges, privilege, shouldAutoMigrate),
    [session.privileges]
  )

  const privilegesForTeam = useCallback(
    (teamId: string) => {
      return getTeamDetails(teamId).privileges
    },
    [getTeamDetails]
  )

  const hasTeamPrivilege = useCallback(
    (teamId: string, privilege: RbacPrivilege, shouldAutoMigrate = true) =>
      getHasMigratedPrivilege(
        privilegesForTeam(teamId),
        privilege,
        shouldAutoMigrate
      ) ||
      // Some high level roles can manage teams even if not a member
      hasCompanyPrivilege(privilege, shouldAutoMigrate),
    [hasCompanyPrivilege, privilegesForTeam]
  )

  const authContextValue: AuthContextValues = useMemo(() => {
    const currentTeam = getTeamDetails(currentTeamId)
    const currentRoles = session.roles.concat(currentTeam.roles)

    const isRunningInCypress = Boolean(window.Cypress)

    return {
      ...getEntitlementsForAuth(session),
      RBAC,
      authHeader: session.header,
      companyDisplayName: session.company_alias || session.company_name,
      companyId: session.company_id,
      companyIsAlfiEnabled: isRunningInCypress
        ? session.company_is_alfi_enabled
        : DEFAULT_AUTH_VALUES.companyIsAlfiEnabled, // todo: EN-5114 remove
      companyPrefs: session.prefs,
      companyPrivileges: session.privileges,
      currentTeam,
      currentTeamId,
      currentTeamRoles: currentTeam.roles,
      endSession,
      fetchSession,
      getTeamDetails,
      hasCompanyPrivilege,
      // todo EN-6460 - after migration remove hasElevatedCompanyRole
      hasElevatedCompanyRole: currentRoles
        .filter(
          (role) =>
            role !== RbacRoleGremlinInternalCompany.DEV &&
            role !== RbacRoleGremlinInternalCompany.ADMIN
        )
        .some((role) => role !== RbacRoleCompany.COMPANY_USER),
      hasTeamPrivilege,
      isProductionEnvironment:
        session?.prefs?.production || currentTeam?.production,
      isRbacEnabled,
      privilegesForTeam,
      ready,
      renewSession,
      roles: currentRoles,
      session,
      setAllAuth,
      setActiveTeamId,
      startSession,
      teams: session.teams
    }
  }, [
    RBAC,
    currentTeamId,
    endSession,
    fetchSession,
    getTeamDetails,
    hasCompanyPrivilege,
    hasTeamPrivilege,
    isRbacEnabled,
    privilegesForTeam,
    ready,
    renewSession,
    session,
    setActiveTeamId,
    startSession
  ])

  useEffectOnce(() => {
    startSession()
  })

  // we need to update the session on a regular basis
  // as the restricted time windows can be updated by another user at any time
  // so we want to be sure that we get those updates quickly to display the warnings
  const SESSION_POLLING_TIME = ms.minutes(10)
  const sessionInterval = useRef<NodeJS.Timer>()
  useEffect(() => {
    if (!sessionInterval.current) {
      sessionInterval.current = setInterval(() => {
        fetchSession()
      }, SESSION_POLLING_TIME)
    }
  }, [SESSION_POLLING_TIME, fetchSession])

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  )
}
